import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import type { LogisticsIncoterm } from '~/graphql/graphql';
import { incoTermToDeliveryMethod } from '~/hooks/useTransportState';

export function ShippingCard({
  shipFrom,
  shippingLocationName,
  addressLine1,
  addressLine2,
  region,
  city,
  zipCode,
  incoTerm,
  mapToggle,
}: Readonly<{
  shipFrom?: string;
  shippingLocationName: string;
  addressLine1: string;
  addressLine2?: string | undefined | null;
  region?: string | null;
  city?: string | null;
  zipCode?: string | null;
  incoTerm?: LogisticsIncoterm | null;
  mapToggle?: ReactNode;
}>) {
  const cityStateZip = [city, region, zipCode].filter(Boolean).join(', ');
  const { t } = useTranslation('order');

  const deliveryMethod = incoTermToDeliveryMethod(incoTerm);
  const deliveryMethodLabel =
    deliveryMethod === 'pickup'
      ? t('order.delivery_method_pickup')
      : deliveryMethod === 'delivered'
        ? t('order.delivery_method_delivered')
        : null;

  return (
    <div
      data-testid="shipping-card"
      className="bg-white rounded-lg border border-neut-200 p-6 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-4">
        <MapPin size={20} className="text-prim-500" />
        <h2 className="text-neut-prim font-semibold">
          {t('order.shipping_information')}
        </h2>
      </div>
      <div className="grid grid-cols-2 gap-6">
        {shipFrom && (
          <div className="space-y-1">
            <p className="text-xs font-semibold text-neut-400 uppercase tracking-wide">
              {t('order.ship_from')}
            </p>
            <p className="text-sm text-neut-prim font-medium">{shipFrom}</p>
          </div>
        )}
        <div className="space-y-1">
          <p className="text-xs font-semibold text-neut-400 uppercase tracking-wide">
            {t('order.ship_to')}
          </p>
          <p className="text-sm text-neut-prim font-medium">
            {shippingLocationName}
          </p>
          <p className="text-sm text-neut-600">{addressLine1}</p>
          {addressLine2 && (
            <p className="text-sm text-neut-600">{addressLine2}</p>
          )}
          {cityStateZip && (
            <p className="text-sm text-neut-600">{cityStateZip}</p>
          )}
          {mapToggle}
        </div>
        {deliveryMethodLabel && (
          <div
            data-testid="mode-of-transport"
            className="col-span-2 border-t border-neut-100 pt-4 space-y-1"
          >
            <p className="text-xs font-semibold text-neut-400 uppercase tracking-wide">
              {t('order.mode_of_transport')}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-neut-prim font-medium">
                {deliveryMethodLabel}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
